import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';

import { LICENSED_STATE_CODES, LICENSED_STATES } from '../../constants/marketExpansion';

// Code → display name, paired positionally with the mirror so a second state
// cannot arrive with a code and no label.
const STATE_LABELS = Object.fromEntries(LICENSED_STATE_CODES.map((c, i) => [c, LICENSED_STATES[i] || c]));
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 46,
  padding: '0 14px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const focusBorder = (e) => (e.target.style.borderColor = '#3a6ad9');
const blurBorder = (e) => (e.target.style.borderColor = '#e5e7eb');

// ── 🔑 THE QUICK CHECK ON THE SHARED RULES (Check Eligibility fix 3, push C — backend constants/quickCheckRules) ──────
// This form asked its own yes/no questions — "a 560+ credit score?", "funds for 10% down?", "a single-family home?" —
// and refused on its own price bounds ($150,000–$3,000,000, the LOAN maximum typed as a price). The options, the price
// bounds and the answer now come from GET /api/uw-matrix/public/quick-check and POST …/quick-check/estimate, the same rules the
// borrower /pre-qualify reads (backend/test/prequalSharedRules.test.js fails on a rule typed here again).
// ⚠️ The stored columns are unchanged (buyer_referrals.prequal_*): their booleans are derived from the answers and the
// server's estimate. prequal_single_family_home now means "an eligible property type" — the column's name is legacy.

export default function DoorlyProNewReferral() {
  const navigate = useNavigate();
  const { token } = useDoorlyProAuth();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    purchase_price_estimate: '',
    prequal_primary_residence: null,
    prequal_under_contract: null,
    prequal_state: '',
    prequal_timeline: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  // The shared rules and the estimate for the current answers (both the server's).
  const [rules, setRules] = useState(null);
  const [rulesError, setRulesError] = useState(false);
  const [quick, setQuick] = useState({ credit: '', down_payment: '', property_type: '' });
  const [verdict, setVerdict] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/uw-matrix/public/quick-check`)
      .then((r) => { if (live) setRules(r.data); })
      .catch(() => { if (live) setRulesError(true); });
    return () => { live = false; };
  }, []);

  // Ask for the estimate whenever an answer changes (only the answers given are checked).
  const priceDigits = String(form.purchase_price_estimate || '').replace(/[^0-9]/g, '');
  useEffect(() => {
    if (!rules) return undefined;
    let live = true;
    const answers = {
      state: form.prequal_state || undefined,
      primary_residence: form.prequal_primary_residence === null ? undefined : (form.prequal_primary_residence ? 'yes' : 'no'),
      property_type: quick.property_type || undefined,
      purchase_price: priceDigits || undefined,
      credit: quick.credit || undefined,
      down_payment: quick.down_payment || undefined,
      audience: 'partner',   // the estimate lines speak about the buyer ("Their estimated minimum…")
    };
    const t = setTimeout(() => {
      axios.post(`${API_URL}/api/uw-matrix/public/quick-check/estimate`, answers)
        .then((r) => { if (live) setVerdict(r.data); })
        .catch(() => { if (live) setVerdict({ outcome: 'error' }); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [rules, form.prequal_state, form.prequal_primary_residence, quick, priceDigits]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const notAFit = !!(verdict && verdict.outcome === 'not_a_fit');
  const priceReason = verdict && (verdict.reason === 'price_low' || verdict.reason === 'price_high') ? verdict.body : null;
  const hasDisqualifying = notAFit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name?.trim()) { setError('First name is required.'); return; }
    if (!form.last_name?.trim()) { setError('Last name is required.'); return; }
    if (!form.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('A valid email address is required.'); return; }
    if (!form.phone?.trim()) { setError('Phone number is required.'); return; }
    if (!form.purchase_price_estimate?.toString().trim()) { setError('Est. Purchase Price is required.'); return; }
    if (!rules) { setError("The quick check couldn't load. Please refresh the page and try again."); return; }
    if (priceReason) { setError(priceReason); return; }
    if (!form.prequal_state) { setError('Please select a purchase state.'); return; }
    if (!form.prequal_timeline) { setError('Please select a timeline.'); return; }
    if (form.prequal_primary_residence === null || !quick.credit || !quick.down_payment || !quick.property_type) { setError('Please answer all pre-qualification questions.'); return; }
    if (!verdict || verdict.outcome === 'error' || verdict.outcome === 'continue') { setError("We couldn't check this buyer's answers just now. Please try again."); return; }
    if (hasDisqualifying) { setError('Based on these answers, this buyer doesn\'t fit the program estimate. See the note above.'); return; }

    // 🔑 The stored booleans, derived from the answers and the server's estimate (never a threshold of our own).
    const credit = (rules.creditOptions || []).find((o) => o.code === quick.credit) || {};
    const property = (rules.propertyTypes || []).find((o) => o.code === quick.property_type) || {};
    const payload = {
      ...form,
      prequal_credit_above_520: credit.scoreFloor != null ? true : credit.code === 'below_floor' ? false : null,
      prequal_has_down_payment: verdict.outcome === 'estimate_fit' ? true : verdict.reason === 'downpayment' ? false : null,
      prequal_single_family_home: property.eligible === true,
    };

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/doorlypro/referrals`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/doorlypro/referrals');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit buyer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 600 }}>
          <div style={{ marginBottom: 24 }}>
            <Link to="/doorlypro/referrals" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
              &larr; Back to My Buyers
            </Link>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>Submit a Buyer</h1>
            <p style={{ fontSize: 14, color: '#6b7280' }}>Submit the form below to invite your buyer to register with Doorly.</p>
          </div>

          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28 }}>
            {error && (
              <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name <span style={{ color: '#ff082f' }}>*</span></label>
                  <input name="first_name" type="text" value={form.first_name} onChange={handleChange} placeholder="Jane" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name <span style={{ color: '#ff082f' }}>*</span></label>
                  <input name="last_name" type="text" value={form.last_name} onChange={handleChange} placeholder="Doe" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span style={{ color: '#ff082f' }}>*</span></label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="client@example.com" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone <span style={{ color: '#ff082f' }}>*</span></label>
                <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Est. Purchase Price <span style={{ color: '#ff082f' }}>*</span></label>
                <input name="purchase_price_estimate" type="text" inputMode="numeric" value={form.purchase_price_estimate}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    setForm(p => ({ ...p, purchase_price_estimate: raw }));
                    setError('');
                    setPriceError(null);
                  }}
                  placeholder="350000"
                  style={{ ...inputStyle, borderColor: priceError ? '#ff082f' : '#e5e7eb', background: priceError ? '#fff8f8' : '#fff' }}
                  onFocus={focusBorder} onBlur={e => { e.target.style.borderColor = priceError ? '#ff082f' : '#e5e7eb'; }} />
                {(priceError || priceReason) && <div style={{ marginTop: 5, fontSize: 12, color: '#ff082f', fontWeight: 600 }}>{priceError || priceReason}</div>}
                {!(priceError || priceReason) && rules && <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>{rules.priceBounds.minLabel} — {rules.priceBounds.maxLabel}</div>}
              </div>

              {/* Pre-qualification questions */}
              <div style={{ marginBottom: 24, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                  Quick Pre-Qualification
                </div>
                {rulesError && (
                  <div style={{ marginBottom: 12, fontSize: 12, color: '#9f1239' }}>The quick check couldn't load. Please refresh the page.</div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Is this a primary residence purchase? <span style={{ color: '#ff082f' }}>*</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['Yes', 'No'].map(opt => {
                      const v = form.prequal_primary_residence;
                      const on = v === (opt === 'Yes');
                      const bad = v === false && opt === 'No';
                      return (
                        <button key={opt} type="button"
                          onClick={() => setForm(p => ({ ...p, prequal_primary_residence: opt === 'Yes' }))}
                          style={{
                            padding: '7px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            border: `2px solid ${bad ? '#ff082f' : on ? '#3a6ad9' : '#e5e7eb'}`,
                            background: bad ? '#fff0f3' : on ? '#eff6ff' : '#fff',
                            color: bad ? '#ff082f' : on ? '#3a6ad9' : '#6b7280',
                          }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {[
                  { key: 'credit', label: 'Their credit score range', options: rules ? rules.creditOptions : [] },
                  { key: 'down_payment', label: 'The down payment they plan to make', options: rules ? rules.downPaymentOptions : [] },
                  { key: 'property_type', label: 'The type of home they\'re buying', options: rules ? rules.propertyTypes : [] },
                ].map(q => (
                  <div key={q.key} style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{q.label} <span style={{ color: '#ff082f' }}>*</span></label>
                    <select data-testid={`referral-prequal-${q.key}`} value={quick[q.key]} onChange={e => { setQuick(p => ({ ...p, [q.key]: e.target.value })); setError(''); }}
                      style={{ ...inputStyle, height: 42, cursor: 'pointer' }}>
                      <option value="">Select...</option>
                      {q.options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  </div>
                ))}
                {/* 🔑 The server's answer, in the server's words: an estimate, never an approval. */}
                {verdict && verdict.outcome === 'not_a_fit' && !priceReason && (
                  <div data-testid="referral-prequal-verdict" data-outcome="not_a_fit" style={{ marginTop: 8, marginBottom: 12, background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#9f1239', lineHeight: 1.5 }}>
                    {verdict.body}
                  </div>
                )}
                {verdict && verdict.outcome === 'estimate_fit' && (
                  <div data-testid="referral-prequal-verdict" data-outcome="estimate_fit" style={{ marginTop: 8, marginBottom: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                    {(verdict.lines || []).map(l => <div key={l}>{l}</div>)}
                  </div>
                )}

                {/* Informational — NOT a disqualifier and NOT required. A "Yes"
                    surfaces the contract-termination coordination copy and, at
                    invite registration, sets the borrower's Stage to "Under
                    contract" (mirrors the self-serve pre-qual answer). */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Is your borrower currently under contract on a home?</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['Yes', 'No'].map(opt => {
                      const selected = form.prequal_under_contract === (opt === 'Yes');
                      return (
                        <button key={opt} type="button"
                          onClick={() => setForm(p => ({ ...p, prequal_under_contract: opt === 'Yes' }))}
                          style={{
                            padding: '7px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            border: `2px solid ${selected ? '#3a6ad9' : '#e5e7eb'}`,
                            background: selected ? '#eff6ff' : '#fff',
                            color: selected ? '#3a6ad9' : '#6b7280',
                          }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {form.prequal_under_contract === true && (
                    <div style={{ marginTop: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                      Because Doorly purchases the home on your borrower's behalf as the new buyer, they'll need to terminate their current purchase contract once they're approved — before Doorly submits its offer. Doorly will coordinate the timing with them.
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Purchase State <span style={{ color: '#ff082f' }}>*</span></label>
                    <select name="prequal_state" value={form.prequal_state} onChange={handleChange}
                      style={{ ...inputStyle, height: 42, cursor: 'pointer' }}>
                      {/* ── 🚨 THIS OFFERED FLORIDA, AND SOMEBODY USED IT ──────
                          On 2026-09-28 a partner submitted a real buyer with a
                          407 (Orlando) number and Purchase State FL. She was
                          emailed "…has invited you to Doorly — Register Now"
                          and it was delivered. Doorly holds a Texas licence;
                          taking the application is itself the licensed activity
                          under Fla. Stat. Ch. 494.

                          🔑 THE DROPDOWN IS THE CONVENIENCE; THE REFUSAL IS THE
                          CONTROL. POST /api/doorlypro/referrals now rejects an
                          unlicensed state against PROGRAM.states. This list is
                          derived so the two cannot disagree. */}
                      <option value="">Select...</option>
                      {LICENSED_STATE_CODES.map((c) => (
                        <option key={c} value={c}>{STATE_LABELS[c] || c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Timeline <span style={{ color: '#ff082f' }}>*</span></label>
                    <select name="prequal_timeline" value={form.prequal_timeline} onChange={handleChange}
                      style={{ ...inputStyle, height: 42, cursor: 'pointer' }}>
                      <option value="">Select...</option>
                      <option value="ready_now">Ready Now</option>
                      <option value="1_3_months">1–3 Months</option>
                      <option value="3_6_months">3–6 Months</option>
                      <option value="6_plus_months">6+ Months</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  disabled={loading || hasDisqualifying}
                  className="btn-primary text-white hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: loading || hasDisqualifying ? '#e5e7eb' : '#3a6ad9', color: loading || hasDisqualifying ? '#9ca3af' : '#fff', flex: 1 }}
                >
                  {loading ? 'Submitting...' : (
                    <>
                      <span>Submit Buyer</span>
                      <span className="btn-arrow">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                      </span>
                    </>
                  )}
                </button>
                <Link
                  to="/doorlypro/referrals"
                  className="btn-secondary"
                  style={{ border: '1px solid #e5e7eb', color: '#374151', textDecoration: 'none', padding: '0 20px', display: 'inline-flex', alignItems: 'center' }}
                >
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
